"use client";

import React, { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Info, Loader2, Trash2, Upload, Video, Weight as WeightIcon, DollarSign } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import Button from "@/components/ui/button/Button";
import Label from "@/components/form/Label";
import RichTextEditor from "@/components/blog/RichTextEditor";
import {
  useUploadProductImage,
  useUploadProductVideo,
  useRemoveProductImage,
  useRemoveProductVideo,
} from "@/lib/productCatalog";
import type { ProductMedia } from "@/types/ecommerce";

export interface ProductEditValues {
  name: string;
  slug: string;
  description: string;
  sku: string;
  brand: string;
  status: string;
  isFeatured: boolean;
  categoryIds: string[];
  tags: string;
  variants: string;
  images: ProductMedia[];
  videos: ProductMedia[];
  price: string | number;
  compareAtPrice: string | number;
  quantity: string | number;
  minStockLevel: string | number;
  weight: string | number;
  length: string | number;
  width: string | number;
  height: string | number;
}

interface ProductEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Present in edit mode only — a product must exist before media can be attached to it
   * (POST /products/{id}/images|videos), so Images/Videos show a real file-picker only when
   * this is set; in create mode they show a "save first" hint instead. */
  productId?: string;
  categoryOptions: { value: string; label: string }[];
  initialValues: Partial<ProductEditValues>;
  onSubmit: (values: ProductEditValues) => Promise<void>;
  submitLabel?: string;
}

const STATUS_OPTIONS = ["Draft", "Published", "Archived"];

const TABS = [
  { key: "details", label: "Details", icon: Info },
  { key: "images", label: "Images", icon: ImageIcon },
  { key: "videos", label: "Videos", icon: Video },
  { key: "price", label: "Price", icon: DollarSign },
  { key: "weight", label: "Weight", icon: WeightIcon },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const inputClass =
  "mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20";

const emptyValues: ProductEditValues = {
  name: "",
  slug: "",
  description: "",
  sku: "",
  brand: "",
  status: "Draft",
  isFeatured: false,
  categoryIds: [],
  tags: "",
  variants: "",
  images: [],
  videos: [],
  price: "",
  compareAtPrice: "",
  quantity: 0,
  minStockLevel: 0,
  weight: "",
  length: "",
  width: "",
  height: "",
};

/** Renders the actual image and falls back to a broken-image icon on load failure — real
 * extensionless CDN URLs (Unsplash, most image hosts) can't be reliably classified by a
 * file-extension regex, so this tries the image itself instead of guessing from the URL shape. */
function ImageThumb({ url }: { url: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-gray-700">
        <ImageIcon className="h-4 w-4" />
      </div>
    );
  }
  return (
    <img
      src={url}
      alt=""
      onError={() => setFailed(true)}
      className="h-10 w-10 shrink-0 rounded-md object-cover bg-gray-100 dark:bg-gray-700"
    />
  );
}

/** Real file-upload editor shared by the Images and Videos tabs — each item is already a
 * saved ProductImage/ProductVideo row (uploaded via ProductMediaController), not a pending
 * form value, so add/remove act immediately against the server rather than waiting for the
 * outer form's Save button. */
function MediaUploadEditor({
  kind,
  items,
  onUpload,
  onRemove,
  accept,
  hint,
  emptyHint,
}: {
  kind: "image" | "video";
  items: ProductMedia[];
  onUpload: (file: File) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
  accept: string;
  hint: string;
  emptyHint: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      await onUpload(file);
    } catch (err) {
      setError((err as Error)?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    setError("");
    try {
      await onRemove(id);
    } catch (err) {
      setError((err as Error)?.message || "Failed to remove. Please try again.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="space-y-2">
      {items.length === 0 && <p className="text-sm text-gray-400">{emptyHint}</p>}
      {items.map((item) => (
        <div key={item.id} className="flex items-center gap-2">
          {kind === "image" ? (
            <ImageThumb url={item.url} />
          ) : (
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-gray-700">
              <Video className="h-4 w-4" />
            </div>
          )}
          <span className="flex-1 truncate text-sm text-gray-600 dark:text-gray-300" title={item.url}>
            {item.url}
          </span>
          <button
            type="button"
            onClick={() => handleRemove(item.id)}
            disabled={removingId === item.id}
            className="shrink-0 rounded-lg p-2 text-red-500 hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-900/20"
            title="Remove"
          >
            {removingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      ))}

      <label className="flex cursor-pointer items-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-gray-400 hover:text-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-500">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {uploading ? "Uploading…" : `Upload ${kind}`}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
          className="hidden"
        />
      </label>
      <p className="text-xs text-gray-400">{hint}</p>
      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

export default function ProductEditModal({
  isOpen,
  onClose,
  title,
  description,
  productId,
  categoryOptions,
  initialValues,
  onSubmit,
  submitLabel = "Save",
}: ProductEditModalProps) {
  const [tab, setTab] = useState<TabKey>("details");
  const [values, setValues] = useState<ProductEditValues>({ ...emptyValues, ...initialValues });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const uploadImage = useUploadProductImage();
  const uploadVideo = useUploadProductVideo();
  const removeImage = useRemoveProductImage();
  const removeVideo = useRemoveProductVideo();

  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setValues({ ...emptyValues, ...initialValues });
      setTab("details");
      setError("");
    }
    wasOpen.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setField = <K extends keyof ProductEditValues>(key: K, value: ProductEditValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleCategory = (id: string) =>
    setValues((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(id) ? prev.categoryIds.filter((c) => c !== id) : [...prev.categoryIds, id],
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.name.trim()) {
      setTab("details");
      setError("Name is required");
      return;
    }
    if (!values.sku.trim()) {
      setTab("details");
      setError("SKU is required");
      return;
    }
    if (values.categoryIds.length === 0) {
      setTab("details");
      setError("At least one category is required");
      return;
    }
    if (values.price === "" || values.price == null) {
      setTab("price");
      setError("Price is required");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError((err as Error)?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-2xl">
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white p-6 dark:bg-gray-900">
        <div className="mb-4">
          <h4 className="text-xl font-semibold text-gray-800 dark:text-white/90">{title}</h4>
          {description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
        </div>

        <div className="mb-4 flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                tab === key
                  ? "border-blue-600 text-blue-600 dark:text-blue-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
              {key === "images" && values.images.length > 0 && (
                <span className="rounded-full bg-gray-100 px-1.5 text-xs dark:bg-gray-700">{values.images.length}</span>
              )}
              {key === "videos" && values.videos.length > 0 && (
                <span className="rounded-full bg-gray-100 px-1.5 text-xs dark:bg-gray-700">{values.videos.length}</span>
              )}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-4">
            {tab === "details" && (
              <>
                <div>
                  <Label htmlFor="name" required>Name</Label>
                  <input id="name" value={values.name} onChange={(e) => setField("name", e.target.value)} disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="slug">Slug</Label>
                  <input id="slug" value={values.slug} onChange={(e) => setField("slug", e.target.value)} placeholder="Auto-generated from name if left blank" disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <RichTextEditor value={values.description} onChange={(html) => setField("description", html)} />
                </div>
                <div>
                  <Label htmlFor="sku" required>SKU</Label>
                  <input id="sku" value={values.sku} onChange={(e) => setField("sku", e.target.value)} disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="brand">Brand</Label>
                  <input id="brand" value={values.brand} onChange={(e) => setField("brand", e.target.value)} maxLength={100} placeholder="Optional — shown in the storefront's Shop by Brand" disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="status" required>Status</Label>
                  <select id="status" value={values.status} onChange={(e) => setField("status", e.target.value)} disabled={submitting} className={inputClass}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                  <input type="checkbox" checked={values.isFeatured} onChange={(e) => setField("isFeatured", e.target.checked)} disabled={submitting} className="rounded border-gray-300 dark:border-gray-600" />
                  Featured — shown in the storefront's Featured Products section
                </label>
                <div>
                  <Label required>Categories</Label>
                  <div className="mt-1 max-h-32 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 space-y-1">
                    {categoryOptions.length === 0 ? (
                      <p className="px-2 py-1 text-xs text-gray-400">No categories available.</p>
                    ) : (
                      categoryOptions.map((opt) => (
                        <label key={opt.value} className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700">
                          <input type="checkbox" checked={values.categoryIds.includes(opt.value)} onChange={() => toggleCategory(opt.value)} disabled={submitting} className="rounded border-gray-300 dark:border-gray-600" />
                          {opt.label}
                        </label>
                      ))
                    )}
                  </div>
                  <p className="mt-1 text-xs text-gray-400">At least one category is required so storefront filters stay meaningful.</p>
                </div>
                <div>
                  <Label htmlFor="tags">Tags</Label>
                  <textarea id="tags" rows={2} value={values.tags} onChange={(e) => setField("tags", e.target.value)} disabled={submitting} className={inputClass} />
                  <p className="mt-1 text-xs text-gray-400">One tag per line</p>
                </div>
                <div>
                  <Label htmlFor="variants">Variants</Label>
                  <textarea id="variants" rows={2} value={values.variants} onChange={(e) => setField("variants", e.target.value)} disabled={submitting} className={inputClass} />
                  <p className="mt-1 text-xs text-gray-400">One per line: name|sku|price|quantity — leave blank for a single-SKU product</p>
                </div>
              </>
            )}

            {tab === "images" && (
              productId ? (
                <MediaUploadEditor
                  kind="image"
                  items={values.images}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  hint="JPEG, PNG, GIF or WEBP, up to 8MB."
                  emptyHint="No images yet — add at least one so the product isn't a blank placeholder on the storefront."
                  onUpload={async (file) => {
                    const media = await uploadImage.mutateAsync({ productId, file });
                    setField("images", [...values.images, media]);
                  }}
                  onRemove={async (imageId) => {
                    await removeImage.mutateAsync({ productId, imageId });
                    setField("images", values.images.filter((i) => i.id !== imageId));
                  }}
                />
              ) : (
                <p className="text-sm text-gray-400">Save the product first, then come back here to upload images.</p>
              )
            )}

            {tab === "videos" && (
              productId ? (
                <MediaUploadEditor
                  kind="video"
                  items={values.videos}
                  accept="video/mp4,video/webm,video/quicktime"
                  hint="MP4, WEBM or MOV, up to 100MB."
                  emptyHint="No videos — optional. Uploaded videos play inline on the product page."
                  onUpload={async (file) => {
                    const media = await uploadVideo.mutateAsync({ productId, file });
                    setField("videos", [...values.videos, media]);
                  }}
                  onRemove={async (videoId) => {
                    await removeVideo.mutateAsync({ productId, videoId });
                    setField("videos", values.videos.filter((v) => v.id !== videoId));
                  }}
                />
              ) : (
                <p className="text-sm text-gray-400">Save the product first, then come back here to upload videos.</p>
              )
            )}

            {tab === "price" && (
              <>
                <div>
                  <Label htmlFor="price" required>Price</Label>
                  <input id="price" type="number" step="0.01" value={values.price} onChange={(e) => setField("price", e.target.valueAsNumber)} disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="compareAtPrice">Compare-at price</Label>
                  <input id="compareAtPrice" type="number" step="0.01" value={values.compareAtPrice} onChange={(e) => setField("compareAtPrice", e.target.valueAsNumber)} placeholder="Optional — shown as a strikethrough" disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="quantity" required>Quantity on hand</Label>
                  <input id="quantity" type="number" value={values.quantity} onChange={(e) => setField("quantity", e.target.valueAsNumber)} disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="minStockLevel" required>Low-stock threshold</Label>
                  <input id="minStockLevel" type="number" value={values.minStockLevel} onChange={(e) => setField("minStockLevel", e.target.valueAsNumber)} disabled={submitting} className={inputClass} />
                </div>
              </>
            )}

            {tab === "weight" && (
              <>
                <div>
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <input id="weight" type="number" step="0.001" value={values.weight} onChange={(e) => setField("weight", e.target.valueAsNumber)} disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="length">Length (cm)</Label>
                  <input id="length" type="number" step="0.1" value={values.length} onChange={(e) => setField("length", e.target.valueAsNumber)} disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="width">Width (cm)</Label>
                  <input id="width" type="number" step="0.1" value={values.width} onChange={(e) => setField("width", e.target.valueAsNumber)} disabled={submitting} className={inputClass} />
                </div>
                <div>
                  <Label htmlFor="height">Height (cm)</Label>
                  <input id="height" type="number" step="0.1" value={values.height} onChange={(e) => setField("height", e.target.valueAsNumber)} disabled={submitting} className={inputClass} />
                </div>
              </>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={submitting}>
              {submitLabel}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
